import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { loadBossImageCatalog, loadNikkeCatalog } from '../../nikke-catalog/api/nikkeCatalogApi.js';
import { applyBossImageFallback } from '../../nikke-catalog/domain/bossImageResolver.js';
import { normalizeRecommendedDeck } from '../../recommended-decks/domain/recommendedDecks.js';
import {
  mergeRaidOverviewRecordDelta,
  raidOverviewDeltaNeedsSnapshot,
} from '../domain/raidOverviewDelta.js';
import {
  buildRaidBossOverview,
  buildRaidLobbySummary,
  buildRaidSeasonMembers,
  dedupeRaidOverviewRecords,
} from '../domain/raidOverviewEngine.js';
import { normalizeRaidOverviewDeck } from '../domain/raidOverviewDeck.js';

const OVERVIEW_ERRORS = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  active_union_membership_required: '활성 유니온 가입 정보가 필요합니다.',
};

const RAID_RECORD_SELECT = 'record_key,member_openid,member_nickname,boss_no,phase_no,attack_no,damage_eok,is_kill,attacked_at,char_level,deck,source_cursor,created_at,updated_at';
const RAID_RECORD_DELTA_LIMIT = 5000;
const RAID_SYNC_WINDOW_LIMIT = 64;
const raidOverviewRequests = new Map();

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('서비스 연결 설정을 확인할 수 없습니다.');
  return supabase;
}

function createOverviewError(error) {
  const raw = String(error?.message || '');
  const code = Object.keys(OVERVIEW_ERRORS).find((item) => raw.includes(item));
  const nextError = new Error(OVERVIEW_ERRORS[code] || raw || '레이드 현황을 불러오지 못했습니다.');
  nextError.code = code || error?.code || 'unknown_error';
  return nextError;
}

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function normalizeAttack(attack, bossNameMap, nikkeCatalog) {
  return {
    recordKey: String(attack?.record_key ?? attack?.recordKey ?? ''),
    memberOpenid: String(attack?.member_openid ?? attack?.memberOpenid ?? ''),
    nickname: String(attack?.member_nickname ?? attack?.nickname ?? '닉네임 확인 불가'),
    bossNo: numberValue(attack?.boss_no ?? attack?.bossNo),
    bossName: bossNameMap.get(numberValue(attack?.boss_no ?? attack?.bossNo)) || String(attack?.bossName || ''),
    phaseNo: numberValue(attack?.phase_no ?? attack?.phaseNo),
    attackNo: (attack?.attack_no ?? attack?.attackNo) === null || (attack?.attack_no ?? attack?.attackNo) === undefined
      ? null
      : numberValue(attack?.attack_no ?? attack?.attackNo),
    damageEok: numberValue(attack?.damage_eok ?? attack?.damageEok),
    isKill: Boolean(attack?.is_kill ?? attack?.isKill),
    attackedAt: attack?.attacked_at ?? attack?.attackedAt ?? attack?.updated_at ?? attack?.updatedAt ?? null,
    updatedAt: attack?.updated_at ?? attack?.updatedAt ?? attack?.created_at ?? attack?.createdAt ?? null,
    charLevel: numberValue(attack?.char_level ?? attack?.charLevel, 0) || null,
    deck: normalizeRaidOverviewDeck(attack?.deck, nikkeCatalog),
  };
}

function normalizeFallbackMembers(raw) {
  const selected = raw?.selected_season && typeof raw.selected_season === 'object' ? raw.selected_season : null;
  return Array.isArray(selected?.members) ? selected.members : [];
}

function liveFallbackMembers(selectedSeason) {
  return (Array.isArray(selectedSeason?.members) ? selectedSeason.members : []).map((member) => ({
    ...member,
    attackCount: 0,
    attack_count: 0,
    remainingAttacks: 3,
    remaining_attacks: 3,
    totalDamageEok: 0,
    total_damage_eok: 0,
    lastAttackAt: null,
    last_attack_at: null,
    hasSeasonRecord: false,
  }));
}

function pickSelectedSeason(seasons, requestedSeasonNo) {
  if (!seasons.length) return null;
  if (requestedSeasonNo) return seasons.find((season) => season.season_no === requestedSeasonNo) || null;
  return seasons.find((season) => season.status === 'active') || seasons[0];
}

function isMissingSettlementCommentColumn(error) {
  const code = String(error?.code || '');
  const message = String(error?.message || '').toLowerCase();
  return code === '42703' || (message.includes('settlement_comment') && message.includes('column'));
}

async function loadSeasonRows(client, unionId) {
  const withComment = await client
    .from('raid_seasons')
    .select('id,season_no,status,settlement_comment')
    .eq('union_id', unionId)
    .order('season_no', { ascending: false });

  if (!withComment.error) return withComment.data || [];
  if (!isMissingSettlementCommentColumn(withComment.error)) throw withComment.error;

  const fallback = await client
    .from('raid_seasons')
    .select('id,season_no,status')
    .eq('union_id', unionId)
    .order('season_no', { ascending: false });
  if (fallback.error) throw fallback.error;
  return (fallback.data || []).map((season) => ({ ...season, settlement_comment: '' }));
}

function overviewBossRows(selectedSeason) {
  return (Array.isArray(selectedSeason?.bosses) ? selectedSeason.bosses : []).map((boss) => ({
    boss_no: Number(boss.bossNo || 0),
    boss_name: String(boss.bossName || ''),
    element: String(boss.element || ''),
    image_url: String(boss.imageUrl || ''),
    boss_comment: String(boss.comment || ''),
    coefficient: Number(boss.coefficient || 1),
  }));
}

function overviewPhaseRows(selectedSeason) {
  return (Array.isArray(selectedSeason?.bosses) ? selectedSeason.bosses : []).flatMap((boss) =>
    (Array.isArray(boss.phases) ? boss.phases : []).map((phase) => ({
      boss_no: Number(boss.bossNo || phase.bossNo || 0),
      phase_no: Number(phase.phaseNo || 0),
      max_hp_eok: phase.maxHpEok,
      is_infinite: Boolean(phase.isInfinite),
    })),
  );
}

async function rebuildOverviewFromRecords(currentOverview, rawRecords, lastSyncAt) {
  const selectedSeason = currentOverview?.selectedSeason;
  if (!selectedSeason) return currentOverview;

  const cleanRecords = dedupeRaidOverviewRecords(rawRecords || []);
  const bossOverview = buildRaidBossOverview({
    bosses: overviewBossRows(selectedSeason),
    phases: overviewPhaseRows(selectedSeason),
    records: cleanRecords,
  });
  const members = buildRaidSeasonMembers({
    records: cleanRecords,
    fallbackMembers: liveFallbackMembers(selectedSeason),
    maxMembers: 32,
  });
  const lobby = buildRaidLobbySummary({ bossOverview, members });
  const bossNameMap = new Map(bossOverview.bosses.map((boss) => [boss.bossNo, boss.bossName]));
  const nikkeCatalog = await loadNikkeCatalog().then((catalog) => catalog.lookup).catch(() => null);
  const records = cleanRecords
    .map((attack) => normalizeAttack(attack, bossNameMap, nikkeCatalog))
    .sort((a, b) => (a.attackNo || 99) - (b.attackNo || 99)
      || String(a.attackedAt || '').localeCompare(String(b.attackedAt || ''))
      || a.nickname.localeCompare(b.nickname, 'ko'));
  const recentAttacks = records
    .slice()
    .sort((a, b) => Date.parse(b.attackedAt || '') - Date.parse(a.attackedAt || ''))
    .slice(0, 20);

  return {
    ...currentOverview,
    selectedSeason: {
      ...selectedSeason,
      lastSyncAt,
      globalPhase: bossOverview.globalPhase,
      bosses: bossOverview.bosses,
      members,
      records,
      recentAttacks,
      ...lobby,
    },
  };
}

function updateRaidOverviewSyncTimestamp(currentOverview, lastSyncAt) {
  if (!currentOverview?.selectedSeason) return currentOverview;
  return {
    ...currentOverview,
    selectedSeason: {
      ...currentOverview.selectedSeason,
      lastSyncAt,
    },
  };
}

async function loadRaidRecordSnapshot(client, unionId, seasonId) {
  const result = await client
    .from('raid_records')
    .select(RAID_RECORD_SELECT)
    .eq('union_id', unionId)
    .eq('season_id', seasonId)
    .limit(RAID_RECORD_DELTA_LIMIT);
  if (result.error) throw createOverviewError(result.error);
  return result.data || [];
}

async function loadRaidOverviewCanonical(requested) {
  const client = requireClient();
  const bossCatalogPromise = loadBossImageCatalog()
    .then((catalog) => catalog.lookup)
    .catch(() => null);
  const nikkeCatalogPromise = loadNikkeCatalog()
    .then((catalog) => catalog.lookup)
    .catch(() => null);
  const { data: base, error: baseError } = await client.rpc('get_union_raid_overview', {
    requested_season_no: requested,
  });
  if (baseError) throw createOverviewError(baseError);

  const unionId = String(base?.union_id || '');
  if (!unionId) throw createOverviewError(new Error('active_union_membership_required'));

  let seasons;
  try {
    seasons = await loadSeasonRows(client, unionId);
  } catch (error) {
    throw createOverviewError(error);
  }

  const selected = pickSelectedSeason(seasons, requested);
  if (!selected) {
    return {
      unionId,
      unionName: String(base?.union_name || ''),
      viewerRole: String(base?.viewer_role || ''),
      seasons: [],
      selectedSeason: null,
    };
  }

  const [bossResult, phaseResult, recordResult, batchResult, recommendedDeckResult] = await Promise.all([
    client.from('raid_bosses').select('boss_no,boss_name,element,image_url,boss_comment,coefficient').eq('union_id', unionId).eq('season_id', selected.id).order('boss_no'),
    client.from('raid_boss_phases').select('boss_no,phase_no,max_hp_eok,is_infinite').eq('union_id', unionId).eq('season_id', selected.id).order('boss_no').order('phase_no'),
    client.from('raid_records').select(RAID_RECORD_SELECT).eq('union_id', unionId).eq('season_id', selected.id).limit(5000),
    client.from('raid_sync_batches').select('completed_at').eq('union_id', unionId).eq('season_id', selected.id).eq('status', 'success').order('completed_at', { ascending: false }).limit(1),
    client.from('saved_squads').select('id,union_id,title,element,deck_codes,note,created_at,updated_at').eq('union_id', unionId).order('updated_at', { ascending: false }).limit(500),
  ]);

  const firstError = bossResult.error || phaseResult.error || recordResult.error || batchResult.error || recommendedDeckResult.error;
  if (firstError) throw createOverviewError(firstError);

  const cleanRecords = dedupeRaidOverviewRecords(recordResult.data || []);
  const rawBossOverview = buildRaidBossOverview({
    bosses: bossResult.data || [],
    phases: phaseResult.data || [],
    records: cleanRecords,
  });
  const [bossImageLookup, nikkeCatalog] = await Promise.all([bossCatalogPromise, nikkeCatalogPromise]);
  const bossOverview = {
    ...rawBossOverview,
    bosses: applyBossImageFallback(rawBossOverview.bosses, bossImageLookup),
  };
  const bossElementByNo = new Map(bossOverview.bosses.map((boss) => [Number(boss.bossNo), String(boss.element || '')]));
  const recommendedDecks = (recommendedDeckResult.data || []).map((row) => normalizeRecommendedDeck(row, bossElementByNo));
  const members = buildRaidSeasonMembers({
    records: cleanRecords,
    fallbackMembers: normalizeFallbackMembers(base),
    maxMembers: 32,
  });
  const lobby = buildRaidLobbySummary({ bossOverview, members });
  const bossNameMap = new Map(bossOverview.bosses.map((boss) => [boss.bossNo, boss.bossName]));
  const records = cleanRecords
    .map((attack) => normalizeAttack(attack, bossNameMap, nikkeCatalog))
    .sort((a, b) => (a.attackNo || 99) - (b.attackNo || 99)
      || String(a.attackedAt || '').localeCompare(String(b.attackedAt || ''))
      || a.nickname.localeCompare(b.nickname, 'ko'));
  const recentAttacks = records
    .slice()
    .sort((a, b) => Date.parse(b.attackedAt || '') - Date.parse(a.attackedAt || ''))
    .slice(0, 20);

  return {
    unionId,
    unionName: String(base?.union_name || ''),
    viewerRole: String(base?.viewer_role || ''),
    seasons: seasons.map((season) => ({ seasonNo: numberValue(season.season_no), status: String(season.status || 'draft') })),
    selectedSeason: {
      id: String(selected.id || ''),
      seasonNo: numberValue(selected.season_no),
      status: String(selected.status || 'draft'),
      settlementComment: String(selected.settlement_comment || ''),
      lastSyncAt: batchResult.data?.[0]?.completed_at || null,
      globalPhase: bossOverview.globalPhase,
      bosses: bossOverview.bosses,
      members,
      records,
      recentAttacks,
      recommendedDecks,
      ...lobby,
    },
  };
}

export function loadRaidOverview(seasonNo = null) {
  const requested = seasonNo === null || seasonNo === undefined || seasonNo === '' ? null : Number(seasonNo);
  const requestKey = requested == null ? 'current' : String(requested);
  const existing = raidOverviewRequests.get(requestKey);
  if (existing) return existing;
  const request = loadRaidOverviewCanonical(requested);
  raidOverviewRequests.set(requestKey, request);
  request.finally(() => {
    if (raidOverviewRequests.get(requestKey) === request) raidOverviewRequests.delete(requestKey);
  }).catch(() => {});
  return request;
}

export async function loadRaidOverviewLiveState(currentOverview) {
  const unionId = String(currentOverview?.unionId || '').trim();
  const selectedSeason = currentOverview?.selectedSeason;
  const seasonId = String(selectedSeason?.id || '').trim();
  if (!unionId || !seasonId) return currentOverview;

  const client = requireClient();
  const batchResult = await client
    .from('raid_sync_batches')
    .select('completed_at')
    .eq('union_id', unionId)
    .eq('season_id', seasonId)
    .eq('status', 'success')
    .order('completed_at', { ascending: false })
    .limit(1);
  if (batchResult.error) throw createOverviewError(batchResult.error);

  const lastSyncAt = batchResult.data?.[0]?.completed_at || null;
  const previousSyncAt = selectedSeason.lastSyncAt || null;
  if (String(lastSyncAt || '') === String(previousSyncAt || '')) return currentOverview;
  if (!lastSyncAt || !previousSyncAt) {
    const records = await loadRaidRecordSnapshot(client, unionId, seasonId);
    return rebuildOverviewFromRecords(currentOverview, records, lastSyncAt);
  }

  const batchWindowResult = await client
    .from('raid_sync_batches')
    .select('completed_at,removed_count')
    .eq('union_id', unionId)
    .eq('season_id', seasonId)
    .eq('status', 'success')
    .gt('completed_at', previousSyncAt)
    .lte('completed_at', lastSyncAt)
    .order('completed_at', { ascending: true })
    .limit(RAID_SYNC_WINDOW_LIMIT);
  if (batchWindowResult.error) throw createOverviewError(batchWindowResult.error);

  if (raidOverviewDeltaNeedsSnapshot({
    batchRows: batchWindowResult.data || [],
    batchLimit: RAID_SYNC_WINDOW_LIMIT,
  })) {
    const records = await loadRaidRecordSnapshot(client, unionId, seasonId);
    return rebuildOverviewFromRecords(currentOverview, records, lastSyncAt);
  }

  const deltaResult = await client
    .from('raid_records')
    .select(RAID_RECORD_SELECT)
    .eq('union_id', unionId)
    .eq('season_id', seasonId)
    .gt('updated_at', previousSyncAt)
    .lte('updated_at', lastSyncAt)
    .order('updated_at', { ascending: true })
    .limit(RAID_RECORD_DELTA_LIMIT);
  if (deltaResult.error) throw createOverviewError(deltaResult.error);

  const deltaRows = deltaResult.data || [];
  if (raidOverviewDeltaNeedsSnapshot({
    batchRows: batchWindowResult.data || [],
    batchLimit: RAID_SYNC_WINDOW_LIMIT,
    deltaCount: deltaRows.length,
    deltaLimit: RAID_RECORD_DELTA_LIMIT,
  })) {
    const records = await loadRaidRecordSnapshot(client, unionId, seasonId);
    return rebuildOverviewFromRecords(currentOverview, records, lastSyncAt);
  }
  if (!deltaRows.length) return updateRaidOverviewSyncTimestamp(currentOverview, lastSyncAt);

  const mergedRecords = mergeRaidOverviewRecordDelta(selectedSeason.records || [], deltaRows);
  return rebuildOverviewFromRecords(currentOverview, mergedRecords, lastSyncAt);
}
